import React from 'react'
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import { usePartnerTheme } from '../../hooks/usePartnerTheme'
import { supabase } from '../../lib/supabaseClient'

/**
 * PartnerDashboard — Layout partenaire (header + sidebar + <Outlet />)
 * Sert de layout pour toutes les sous-pages partenaire (overview, coupons, orders, members, settings).
 */
const PartnerDashboard: React.FC = () => {
  const { partnerProfile, partner, isAdminPartner } = usePartnerAuth()
  const theme = usePartnerTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = async () => {
    // Forcer le nettoyage local même si le serveur ne reconnaît plus la session
    try {
      await supabase.auth.signOut({ scope: 'local' })
    } catch (_) {
      // Ignorer les erreurs serveur (session_not_found après restart)
    }
    // Nettoyer manuellement le localStorage au cas où
    localStorage.removeItem('sb-auth-token-stringnow')
    navigate('/partner/login', { replace: true })
  }

  if (!partner || !partnerProfile) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p>Chargement des données du partenaire...</p>
      </div>
    )
  }

  const isActive = (path: string) => location.pathname === path

  const navLinkStyle = (path: string): React.CSSProperties => ({
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    textDecoration: 'none',
    color: isActive(path) ? '#334155' : '#475569',
    fontSize: '0.95rem',
    transition: 'all 0.2s',
    background: isActive(path) ? '#e2e8f0' : 'transparent',
    fontWeight: isActive(path) ? '500' : 'normal',
  })

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Header partenaire */}
      <div
        style={{
          background: theme.headerGradient,
          color: 'white',
          padding: '2rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
        data-testid="partner-header"
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {partner.logo_url && (
              <img
                src={partner.logo_url}
                alt={partner.name}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '0.5rem',
                  background: 'rgba(255,255,255,0.1)',
                  padding: '0.25rem',
                }}
              />
            )}
            <div>
              <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '600' }} data-testid="partner-name">
                {partner.name}
              </h1>
              <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                Partner Portal - {isAdminPartner ? 'Admin' : 'Member'}
              </p>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', opacity: 0.9 }}>
              Connecté: {partnerProfile.full_name || partnerProfile.email}
            </p>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)'
              }}
            >
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Contenu main */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '2rem' }}>
          {/* Sidebar navigation */}
          <aside
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              height: 'fit-content',
              position: 'sticky',
              top: '1rem',
            }}
          >
            <h3
              style={{
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#64748b',
                marginBottom: '1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Navigation
            </h3>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Link
                to="/partner/book"
                data-testid="nav-new-booking"
                style={{
                  display: 'block',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.375rem',
                  textDecoration: 'none',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  textAlign: 'center',
                  background: partner?.color_code || '#3b82f6',
                  transition: 'all 0.2s',
                  marginBottom: '0.5rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.85'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1'
                }}
              >
                ➕ Nouvelle réservation
              </Link>

              <Link
                to="/partner/dashboard"
                style={navLinkStyle('/partner/dashboard')}
              >
                📊 Vue d'ensemble
              </Link>

              <Link
                to="/partner/orders"
                data-testid="nav-orders"
                style={navLinkStyle('/partner/orders')}
              >
                📋 Commandes
              </Link>

              {isAdminPartner && (
                <Link
                  to="/partner/members"
                  data-testid="nav-members"
                  style={navLinkStyle('/partner/members')}
                >
                  👥 Membres
                </Link>
              )}

              <Link
                to="/partner/settings"
                style={navLinkStyle('/partner/settings')}
              >
                ⚙️ Paramètres
              </Link>

              <Link
                to="/partner/change-password"
                data-testid="nav-change-password"
                style={navLinkStyle('/partner/change-password')}
              >
                🔒 Mot de passe
              </Link>
            </nav>
          </aside>

          {/* Main content — sous-pages rendues via Outlet */}
          <div>
            <Outlet />
          </div>
        </div>
      </main>

      {/* Footer thématisé */}
      <footer
        data-testid="partner-footer"
        style={{
          background: theme.footerBackground,
          color: 'rgba(255,255,255,0.85)',
          textAlign: 'center',
          padding: '1rem 2rem',
          fontSize: '0.85rem',
          marginTop: 'auto',
        }}
      >
        <p style={{ margin: 0 }}>
          {partner.name} · Partner Portal · © {new Date().getFullYear()} StringNow
        </p>
      </footer>
    </div>
  )
}

export default PartnerDashboard
